import type { CellStyle } from 'ag-grid-community'
import { isObjectType } from 'remeda'
import type { Color } from '../../../types/colors'

/**
 * Cell Highlighting Utilities for AG Grid
 */

/** Cell value that may contain highlight information */
type CellValueWithHighlight = {
  readonly value: unknown
  readonly highlight?: { readonly color: Color }
  readonly [key: string]: unknown
}

/** Extracted highlight result */
type HighlightResult = {
  readonly value: unknown
  readonly highlight: { readonly color: Color } | null
}

/**
 * Extract highlight information from cell value object
 */
export const extractHighlight = (value: unknown): HighlightResult => {
  // Check if value is an object with highlight property
  if (isObjectType(value) && value !== null && 'highlight' in value) {
    const typedValue = value as CellValueWithHighlight

    return {
      // Extract nested value if present, otherwise use the object itself
      value: 'value' in value ? typedValue.value : value,
      highlight: typedValue.highlight ?? null
    }
  }

  // Simple value without highlighting
  return { value, highlight: null }
}

/**
 * Generate inline CSS styles for cell highlighting
 */
export const getHighlightStyle = (color: Color): CellStyle => ({
  backgroundColor: `var(--color-${color}-3)`,
  borderLeft: `3px solid var(--color-${color}-9)`,
  borderRight: `1px solid var(--color-${color}-9)`,
  boxShadow: `inset 0 1px 0 0 var(--color-${color}-9), inset 0 -1px 0 0 var(--color-${color}-9)`,
  color: `var(--color-${color}-11)`,
  // Ghost button cells keep their own label color unless the token follows.
  '--facade-button-ghost-content-color': `var(--color-${color}-11)`,
  fontWeight: '600'
})

/**
 * Get cell style from value with optional highlight
 */
/** Reset styles to clear previous highlight */
const RESET_STYLE: CellStyle = {
  backgroundColor: '',
  borderLeft: '',
  borderRight: '',
  boxShadow: '',
  color: '',
  '--facade-button-ghost-content-color': '',
  fontWeight: ''
}

export const getCellHighlightStyle = (value: unknown): CellStyle | null => {
  const { highlight } = extractHighlight(value)

  if (highlight?.color) {
    return getHighlightStyle(highlight.color)
  }

  // If value is an object with explicit null highlight, reset styles to clear previous highlight
  if (isObjectType(value) && value !== null && 'highlight' in value) {
    return RESET_STYLE
  }

  return null
}

// Export types for external use
export type { CellValueWithHighlight, HighlightResult }
